import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import Breadcrumbs, { generateBreadcrumbSchema } from "../components/shared/Breadcrumbs";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Building2, Clock, Shield, Monitor } from "lucide-react";
import { createServiceSchema, createFAQSchema } from "../seo/schemas";

const OfficeRemovalsPage = () => {
  const features = [
    { icon: Clock, title: "Minimal Disruption", desc: "Weekend and out-of-hours moves available" },
    { icon: Monitor, title: "IT Specialists", desc: "Safe handling of servers and equipment" },
    { icon: Shield, title: "Fully Insured", desc: "Commercial insurance up to £100,000" },
    { icon: Building2, title: "All Business Sizes", desc: "From small offices to corporate HQs" }
  ];

  const breadcrumbs = [
    { label: "Services", href: "/services" },
    { label: "Office Removals", href: "/services/office-removals" },
  ];

  const pageFaqs = [
    { q: "Do you offer out-of-hours office moves?", a: "Yes, we specialise in weekend and evening office relocations to minimise disruption to your business operations. Most office moves are completed outside standard working hours." },
    { q: "Can you handle IT equipment and servers?", a: "Absolutely. Our team is trained in handling IT equipment, servers, and sensitive electronics. We use specialist packaging and anti-static materials to ensure safe transport." },
    { q: "How long does a typical office move take?", a: "This depends on the size of your office. A small office (10-20 desks) typically takes 1-2 days. We'll provide a detailed timeline after your free survey." },
    { q: "Is commercial insurance included?", a: "Yes, all office moves include commercial insurance up to £100,000 as standard. Additional cover is available for high-value equipment." },
  ];

  const jsonLd = [
    createServiceSchema("Office Removals Cardiff", "Professional office and business relocation services in Cardiff. Weekend moves, IT equipment handling, project management included.", "/services/office-removals"),
    createFAQSchema(pageFaqs),
    generateBreadcrumbSchema(breadcrumbs),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Office Removals Cardiff | Business Relocation Experts | Hitech Transit"
        description="Professional office removals in Cardiff. Weekend moves, IT equipment handling, project management included. Fully insured up to £100,000. Free survey and quote."
        canonical="/services/office-removals"
        jsonLd={jsonLd}
      />
      <Header />
      
      <main className="pt-20">
        <Breadcrumbs items={breadcrumbs} />
        {/* Hero */}
        <section className="relative py-20 lg:py-32">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/80ea756f-7ea3-4803-b754-18b59043e07a/images/acf49cfebb293b7cef04a42dfc41de23d515e47416b9f16c3c274dc10541f317.png)`
            }}
          >
            <div className="absolute inset-0 bg-[#0F172A]/70" />
          </div>
          
          <div className="container-max px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-4 block">
                Office Removals
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Outfit'] font-bold text-white tracking-tight mb-6">
                Office Removals & Business Relocation in Cardiff
              </h1>
              <p className="text-slate-200 text-lg mb-8">
                Move your business with minimal downtime. Our commercial removal experts handle 
                everything from planning to execution.
              </p>
              <Link to="/instant-quote" className="btn-primary inline-flex items-center gap-2" data-testid="office-removals-quote-btn">
                Get Your Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="section-padding bg-[#F8F9FA]">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div key={feature.title || feature.name || `feature-${index}`} className="text-center">
                  <div className="w-14 h-14 bg-[#FF5A1F]/10 mx-auto flex items-center justify-center mb-4">
                    <feature.icon className="w-7 h-7 text-[#FF5A1F]" />
                  </div>
                  <h3 className="text-lg font-['Outfit'] font-bold text-[#0F172A] mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="section-padding">
          <div className="container-max">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-6">
                Tailored Office Moving Solutions
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                We understand that moving your business is about more than just relocating furniture. 
                It's about minimizing disruption, protecting sensitive equipment, and ensuring your 
                team can get back to work as quickly as possible.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Our commercial removal service includes detailed project planning, dedicated move 
                managers, and specialized handling for IT equipment and sensitive documents.
              </p>
              
              <div className="space-y-4">
                {[
                  "Detailed pre-move planning and consultation",
                  "Dedicated project manager for your move",
                  "IT equipment and server relocation",
                  "Furniture disassembly and reassembly",
                  "Confidential document handling",
                  "Weekend and evening moves available",
                  "Post-move cleanup and furniture placement"
                ].map((item, index) => (
                  <div key={item.title || item.name || `item-${index}`} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#FF5A1F] flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-12">
                <Link to="/book-survey" className="btn-primary inline-flex items-center gap-2" data-testid="office-book-survey-btn">
                  Book Free Survey
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="section-padding bg-white">
          <div className="container-max">
            <h2 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-8">
              Frequently Asked Questions About Office Removals
            </h2>
            <div className="max-w-3xl space-y-6">
              {pageFaqs.map((faq, index) => (
                <div key={`faq-${index}`} className="border-b border-slate-200 pb-6">
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{faq.q}</h3>
                  <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OfficeRemovalsPage;
